import { gql } from '@apollo/client'

export interface RefreshApprovalFlowSubscriptionVariables {
  dealId: number
}

interface RefreshApprovalFlowApprover {
  approverType: string
  approverDescription: string
  isActiveApprover?: boolean
}

interface RefreshApprovalFlowDecision {
  reasonCode: string
  reasonDesc: string
  decisionTypeCode: string
  decisionTypeDesc: string
}

interface RefreshApprovalFlowGroup {
  groupId: string
  groupName: string
  roleTitle: string
  decisionTypeCode: string
  decisionReasonCode: string
  approvers: RefreshApprovalFlowApprover[]
  decision: RefreshApprovalFlowDecision
}

interface RefreshApprovalFlowRule {
  ruleId: string
  ruleType: string
  ruleName: string
  baseline: string
}

interface RefreshApprovalFlowApproverGroup {
  groupId: string
  group: RefreshApprovalFlowGroup
  ruleIds: string[]
  rules: RefreshApprovalFlowRule[]
}

interface RefreshApprovalFlowApprovalStatus {
  approverGroups: string[]
  approvedBy: string
  approvalDate: string
  comments: string
  approvalRefreshDate: string
  decisionTypeCode: string
  decisionReasonCode: string
}

interface RefreshApprovalFlowApproverLevel {
  level: number
  isLevelComplete: boolean
  approverGroups: RefreshApprovalFlowApproverGroup[]
  approvalStatuses: RefreshApprovalFlowApprovalStatus[]
}

interface RefreshApprovalFlowApprovalGroup {
  groupId: string
  groupName: string
  roleTitle: string
  decisionTypeCode: string
  decisionReasonCode: string
  isTriggerAutomatically: boolean
  isSendEmail: boolean
  approvers: RefreshApprovalFlowApprover[]
}

interface RefreshApprovalFlowProductApprovalStatus {
  approverGroup: string
  approvedBy: string
  approvalDate: string
  comments: string
  approvalRefreshDate: string
  decisionTypeCode: string
  decisionReasonCode: string
}

interface RefreshApprovalFlowProductApprovalLevel {
  level: number
  groups: RefreshApprovalFlowApprovalGroup[]
  approvalStatus: RefreshApprovalFlowProductApprovalStatus
  isLevelComplete: boolean
  isCurrentLevel: boolean
}

interface RefreshApprovalFlowTopApprover {
  level: number
  groups: RefreshApprovalFlowApprovalGroup[]
}

interface RefreshApprovalFlowProductLine {
  productLineCode: string
  appliedRule: string
  rule: RefreshApprovalFlowRule
  approvalLevels: RefreshApprovalFlowProductApprovalLevel[]
  topApprover: RefreshApprovalFlowTopApprover
}

interface RefreshApprovalFlowProduct {
  lineNumber: number
  itemNumber: number
  appliedRule: string
  isProductLineCurrentLevel: boolean
  isProductLineLevelComplete: boolean
  rule: RefreshApprovalFlowRule
  approvalLevels: RefreshApprovalFlowProductApprovalLevel[]
  topApprover: RefreshApprovalFlowTopApprover
  productLineAppliedRule: string
  productLineRule: RefreshApprovalFlowRule
  productLineTopApprover: RefreshApprovalFlowTopApprover
}

interface RefreshApprovalFlowDealInfo {
  appliedRules: string[]
  approvers: RefreshApprovalFlowApproverLevel[]
}

interface RefreshApprovalFlowSummary {
  approvers: RefreshApprovalFlowApproverLevel[]
}

export interface RefreshApprovalFlowPayload {
  dealInfo: RefreshApprovalFlowDealInfo
  summary: RefreshApprovalFlowSummary
  productLines: RefreshApprovalFlowProductLine[]
  products: RefreshApprovalFlowProduct[]
  isAutoApprove: boolean
}

export interface RefreshApprovalFlowSubscriptionResponse {
  onRefreshApprovalFlow: RefreshApprovalFlowPayload
}

export const REFRESH_APPROVAL_FLOW_SUBSCRIPTION = gql`
  subscription onRefreshApprovalFlow($dealId: Int!) {
    onRefreshApprovalFlow(dealId: $dealId) {
      dealInfo {
        appliedRules
        approvers {
          level
          isLevelComplete
          approverGroups {
            groupId
            group(dealId: $dealId) {
              groupId
              groupName
              roleTitle
              decisionTypeCode
              decisionReasonCode
              approvers {
                approverType
                approverDescription
                isActiveApprover
              }
              decision {
                reasonCode
                reasonDesc
                decisionTypeCode
                decisionTypeDesc
              }
            }
            ruleIds
            rules(dealId: $dealId) {
              ruleId
              ruleType
              ruleName
              baseline
            }
          }
          approvalStatuses {
            approverGroups
            approvedBy
            approvalDate
            comments
            approvalRefreshDate
            decisionTypeCode
            decisionReasonCode
          }
        }
      }
      summary {
        approvers {
          level
          isLevelComplete
          approverGroups {
            groupId
            group(dealId: $dealId) {
              groupId
              groupName
              roleTitle
              decisionTypeCode
              decisionReasonCode
              approvers {
                approverType
                approverDescription
                isActiveApprover
              }
              decision {
                reasonCode
                reasonDesc
                decisionTypeCode
                decisionTypeDesc
              }
            }
            ruleIds
            rules(dealId: $dealId) {
              ruleId
              ruleType
              ruleName
              baseline
            }
          }
          approvalStatuses {
            approverGroups
            approvedBy
            approvalDate
            comments
            approvalRefreshDate
            decisionTypeCode
            decisionReasonCode
          }
        }
      }
      productLines {
        productLineCode
        appliedRule
        rule(dealId: $dealId) {
          ruleId
          ruleType
          ruleName
          baseline
        }
        approvalLevels {
          level
          groups {
            groupId
            groupName
            roleTitle
            decisionTypeCode
            decisionReasonCode
            isTriggerAutomatically
            isSendEmail
            approvers {
              approverType
              approverDescription
            }
          }
          approvalStatus {
            approverGroup
            approvedBy
            approvalDate
            comments
            approvalRefreshDate
            decisionTypeCode
            decisionReasonCode
          }
          isLevelComplete
          isCurrentLevel
        }
        topApprover {
          level
          groups {
            groupId
            groupName
            roleTitle
            decisionTypeCode
            decisionReasonCode
            isTriggerAutomatically
            isSendEmail
            approvers {
              approverType
              approverDescription
            }
          }
        }
      }
      products {
        lineNumber
        itemNumber
        appliedRule
        isProductLineCurrentLevel
        isProductLineLevelComplete
        rule(dealId: $dealId) {
          ruleId
          ruleType
          ruleName
          baseline
        }
        approvalLevels {
          level
          groups {
            groupId
            groupName
            roleTitle
            decisionTypeCode
            decisionReasonCode
            isTriggerAutomatically
            isSendEmail
            approvers {
              approverType
              approverDescription
            }
          }
          approvalStatus {
            approverGroup
            approvedBy
            approvalDate
            comments
            approvalRefreshDate
            decisionTypeCode
            decisionReasonCode
          }
          isLevelComplete
          isCurrentLevel
        }
        topApprover {
          level
          groups {
            groupId
            groupName
            roleTitle
            decisionTypeCode
            decisionReasonCode
            isTriggerAutomatically
            isSendEmail
            approvers {
              approverType
              approverDescription
            }
          }
        }
        productLineAppliedRule
        productLineRule(dealId: $dealId) {
          ruleId
          ruleType
          ruleName
          baseline
        }
        productLineTopApprover {
          level
          groups {
            groupId
            groupName
            roleTitle
            decisionTypeCode
            decisionReasonCode
            isTriggerAutomatically
            isSendEmail
            approvers {
              approverType
              approverDescription
            }
          }
        }
      }
      isAutoApprove
    }
  }
`
